import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog'
import { Button } from './button'
import { AlertTriangle, Info, Archive, ArchiveRestore, Trash2, Check, X } from 'lucide-react'

export type ConfirmationType = 'archive' | 'unarchive' | 'delete' | 'warning' | 'info' | 'confirm'

interface ConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  type?: ConfirmationType
  title?: string
  description?: string | React.ReactNode
  confirmText?: string
  cancelText?: string
  isLoading?: boolean
  onConfirm: () => void
  onCancel?: () => void
  itemName?: string
  warningContent?: string | React.ReactNode
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
}

const getTypeConfig = (type: ConfirmationType) => {
  switch (type) {
    case 'archive':
      return {
        icon: Archive,
        iconColor: 'text-orange-600',
        variant: 'destructive' as const,
        defaultTitle: 'Archive Item',
        defaultConfirm: 'Archive',
        defaultDescription: 'Are you sure you want to archive this item?',
      }
    case 'unarchive':
      return {
        icon: ArchiveRestore,
        iconColor: 'text-green-600',
        variant: 'default' as const,
        defaultTitle: 'Unarchive Item',
        defaultConfirm: 'Unarchive',
        defaultDescription: 'Are you sure you want to unarchive this item?',
      }
    case 'delete':
      return {
        icon: Trash2,
        iconColor: 'text-red-600',
        variant: 'destructive' as const,
        defaultTitle: 'Delete Item',
        defaultConfirm: 'Delete',
        defaultDescription: 'Are you sure you want to delete this item? This action cannot be undone.',
      }
    case 'warning':
      return {
        icon: AlertTriangle,
        iconColor: 'text-yellow-600',
        variant: 'destructive' as const,
        defaultTitle: 'Warning',
        defaultConfirm: 'Continue',
        defaultDescription: 'Please confirm this action.',
      }
    case 'info':
      return {
        icon: Info,
        iconColor: 'text-blue-600',
        variant: 'default' as const,
        defaultTitle: 'Information',
        defaultConfirm: 'OK',
        defaultDescription: 'Please review the information below.',
      }
    case 'confirm':
    default:
      return {
        icon: Check,
        iconColor: 'text-green-600',
        variant: 'default' as const,
        defaultTitle: 'Confirm Action',
        defaultConfirm: 'Confirm',
        defaultDescription: 'Are you sure you want to proceed?',
      }
  }
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onOpenChange,
  type = 'confirm',
  title,
  description,
  confirmText,
  cancelText = 'Cancel',
  isLoading = false,
  onConfirm,
  onCancel,
  itemName,
  warningContent,
  variant,
}) => {
  const config = getTypeConfig(type)
  const IconComponent = config.icon
  
  const finalTitle = title || config.defaultTitle
  const finalConfirmText = confirmText || config.defaultConfirm
  const finalVariant = variant || config.variant

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      onOpenChange(false)
    }
  }

  const renderDescription = () => {
    if (description) {
      return typeof description === 'string' ? (
        <span>{description}</span>
      ) : (
        description
      )
    }

    let baseDescription = config.defaultDescription
    if (itemName) {
      baseDescription = baseDescription.replace(
        'this item',
        `"${itemName}"`
      )
    }

    return (
      <>
        <span>{baseDescription}</span>
        {warningContent && (
          <>
            <br />
            <br />
            <div className="flex items-start gap-2 mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
              <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-orange-800">
                {typeof warningContent === 'string' ? (
                  <div dangerouslySetInnerHTML={{ __html: warningContent }} />
                ) : (
                  warningContent
                )}
              </div>
            </div>
          </>
        )}
      </>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconComponent className={`h-5 w-5 ${config.iconColor}`} />
            {finalTitle}
          </DialogTitle>
          <DialogDescription className="text-left">
            {renderDescription()}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            variant={finalVariant}
            onClick={onConfirm}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <IconComponent className="h-4 w-4" />
            )}
            {isLoading ? 'Processing...' : finalConfirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}